import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AdminHeader from "../components/AdminHeader";
import ClientHeader from "../components/ClientHeader";
import NavSidebar from "../components/NavSidebar";
import { usePageMeta } from "../components/DashboardShell";
import { useView } from "../contexts/ViewContext";
import { getCiSummary, getCiAvailableFilters, getCiRateCalendar, getCiReportRevenueOpportunity, getCiActions, ciGetPanelInsights } from "../api/competitiveIntelligence";
import {
  MetricCardShell,
  MetricSkeleton,
  MetricError,
  TONE_STYLES,
} from "../components/competitive-intelligence/MetricCard";
import { AnalysisPanelContext } from "../components/competitive-intelligence/AnalysisPanel";
import ThinScroll from "../components/shared/ThinScroll";
import { Skeleton } from "../components/shared/Skeleton";
import { fetchAllPages } from "../components/shared/fetchAllPages";
import RateCalendarPanel from "../components/competitive-intelligence/RateCalendarPanel";
import RateDetailDrawer from "../components/competitive-intelligence/RateDetailDrawer";
import MarketPositionPanel from "../components/competitive-intelligence/MarketPositionPanel";
import RateChangeFeedPanel from "../components/competitive-intelligence/RateChangeFeedPanel";
import AiPricingActionsPanel from "../components/competitive-intelligence/AiPricingActionsPanel";
import VerdictCard from "../components/competitive-intelligence/VerdictCard";
import { VerdictBlock } from "../components/competitive-intelligence/shared";
import InfoTip from "../components/shared/InfoTip";
import CompetitorProfilesPanel from "../components/competitive-intelligence/CompetitorProfilesPanel";
import DemandSignalsPanel from "../components/competitive-intelligence/demand-timing/DemandSignalsPanel";
import PriceGapsPanel from "../components/competitive-intelligence/pricing-position/PriceGapsPanel";
import WeekdayWeekendPanel from "../components/competitive-intelligence/demand-timing/WeekdayWeekendPanel";
import BookingWindowPanel from "../components/competitive-intelligence/demand-timing/BookingWindowPanel";
import EventSpikesPanel from "../components/competitive-intelligence/demand-timing/EventSpikesPanel";
import SiteComparisonPanel from "../components/competitive-intelligence/pricing-position/SiteComparisonPanel";
import AvailabilityTrendsPanel from "../components/competitive-intelligence/competitor-intel/AvailabilityTrendsPanel";
import PriceDistributionPanel from "../components/competitive-intelligence/competitor-intel/PriceDistributionPanel";
import RevenueOpportunityPanel from "../components/competitive-intelligence/revenue-opportunity/RevenueOpportunityPanel";
import HighValueDatesPanel from "../components/competitive-intelligence/demand-timing/HighValueDatesPanel";
import DynamicPricingPanel from "../components/competitive-intelligence/revenue-opportunity/DynamicPricingPanel";
import CompetitiveRankingPanel from "../components/competitive-intelligence/pricing-position/CompetitiveRankingPanel";
import InventoryConcentrationPanel from "../components/competitive-intelligence/revenue-opportunity/InventoryConcentrationPanel";
import CompetitorVulnerabilityPanel from "../components/competitive-intelligence/competitor-intel/CompetitorVulnerabilityPanel";
import RateIntegrityPanel from "../components/competitive-intelligence/pricing-position/RateIntegrityPanel";
import DowHeatmapPanel from "../components/competitive-intelligence/competitor-intel/DowHeatmapPanel";
import ForwardCalendarPanel from "../components/competitive-intelligence/demand-timing/ForwardCalendarPanel";
import MarketPositionDetailPanel from "../components/competitive-intelligence/pricing-position/MarketPositionDetailPanel";
import DowRevenueHeatmapPanel from "../components/competitive-intelligence/revenue-opportunity/DowRevenueHeatmapPanel";
import AbsorptionVelocityPanel from "../components/competitive-intelligence/absorption-trends/AbsorptionVelocityPanel";
import AbsorptionVsBaselinePanel from "../components/competitive-intelligence/absorption-trends/AbsorptionVsBaselinePanel";
import AbsorptionHistoryPanel from "../components/competitive-intelligence/absorption-trends/AbsorptionHistoryPanel";
import MarketAbsorptionPanel from "../components/competitive-intelligence/absorption-trends/MarketAbsorptionPanel";
import DemandIndexPanel from "../components/competitive-intelligence/absorption-trends/DemandIndexPanel";
import RateTrendsPanel from "../components/competitive-intelligence/RateTrendsPanel";
import HolidayPremiumsPanel from "../components/competitive-intelligence/HolidayPremiumsPanel";

// Bundled async-state shape used for every CI metric fetch.
const initialState = { loading: false, error: "", data: null };

// RPI color scale per spec §1A: <90 red, 90–100 amber, 100–110 emerald, >110 brandBlue (premium).
const rpiTone = (rpi) => {
  if (rpi == null) return "gray";
  if (rpi < 90) return "red";
  if (rpi < 100) return "amber";
  if (rpi <= 110) return "emerald";
  return "brandBlue";
};

// Sparkline stroke colors keyed by the same tone names as TONE_STYLES.
const SPARK_STROKE = {
  emerald: "#10b981",
  amber: "#f59e0b",
  red: "#ef4444",
  gray: "#9ca3af",
  brandBlue: "#32B1FF",
};

const Sparkline = ({ points, stroke }) => {
  if (!points || points.length < 2) return null;
  const width = 120, height = 28;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = max - min || 1;
  const path = points.map((value, index) => {
    const x = (index / (points.length - 1)) * width;
    const y = height - ((value - min) / range) * height;
    return `${index === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
  }).join(" ");
  return (
    <svg width={width} height={height} className="mt-2">
      <path d={path} fill="none" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

const RpiCard = ({ state }) => {
  if (state.loading) return <MetricSkeleton />;
  if (state.error) return <MetricError title="Rate Position" message={state.error} />;

  const metrics = state.data?.metrics ?? {};
  const rpi = metrics.rpi ?? null;
  const tone = rpiTone(rpi);
  const toneStyle = TONE_STYLES[tone];
  const subtitle = rpi == null
    ? "—"
    : `${Math.abs(rpi - 100).toFixed(1)}% ${rpi > 100 ? "above" : "below"} comp set`;
  const you = metrics.client_avg_rate;
  const comp = metrics.comp_set_avg_rate;
  const underlying = (you != null && comp != null)
    ? `Your $${Number(you).toFixed(2)} vs $${Number(comp).toFixed(2)} market`
    : null;

  return (
    <MetricCardShell
      tone={tone}
      icon="target"
      title="Rate Position"
      tooltip="Where your pricing sits relative to your direct comps."
    >
      <div className="mt-2 flex items-baseline gap-2">
        <InfoTip text="How your rate compares to your direct comps. 100 = at market, above 100 = premium, below 100 = below market. (How: your average rate divided by the comp set average, then multiplied by 100.)">
          <span className={`text-3xl font-extrabold leading-none ${toneStyle.valueClass}`}>
            {rpi == null ? "—" : rpi.toFixed(1)}
          </span>
        </InfoTip>
        <span className="text-xs text-gray-400 dark:text-gray-500">RPI</span>
      </div>
      <div className="mt-1 text-sm text-gray-600 dark:text-gray-300">{subtitle}</div>
      {underlying && (
        <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 tabular-nums">{underlying}</div>
      )}
    </MetricCardShell>
  );
};

const OpiCard = ({ state }) => {
  if (state.loading) return <MetricSkeleton />;
  if (state.error) return <MetricError title="Occupancy Position" message={state.error} />;

  const metrics = state.data?.metrics ?? {};
  const opi = metrics.opi ?? null;
  const tone = opi == null ? "gray" : opi < 90 ? "red" : opi <= 110 ? "amber" : "emerald";
  const toneStyle = TONE_STYLES[tone];
  // Phrasing mirrors the Verdict banner ("filling X% slower/faster than comp set") so
  // the verdict and the KPI subtitle don't fight each other when they sit on adjacent rows
  // — both describe the same OPI number with the same words.
  // "Filling X% slower" — matches the verdict banner wording, which also drops
  // the "than comp set" suffix. Card title ("OCCUPANCY POSITION") and the
  // formula tooltip on the value already establish the comparison reference,
  // so the suffix was redundant and made the line wrap on narrow cards while
  // the parallel RPI subtitle stayed single-line.
  const subtitle = opi == null
    ? "—"
    : `Filling ${Math.abs(opi - 100).toFixed(1)}% ${opi > 100 ? "faster" : "slower"}`;
  const youOcc = metrics.client_inferred_occupancy_avg;
  const compOcc = metrics.comp_set_inferred_occupancy_avg;
  const underlying = (youOcc != null && compOcc != null)
    ? `~${Number(youOcc).toFixed(1)}% you vs ~${Number(compOcc).toFixed(1)}% market`
    : null;

  return (
    <MetricCardShell
      tone={tone}
      icon="bell"
      title="Occupancy Position"
      tooltip="How fast you're filling relative to your direct comps."
      
    >
      <div className="mt-2 flex items-baseline gap-2">
        <InfoTip text="How fast you're filling compared to your direct comps. Above 100 = filling faster than competitors, below 100 = slower. (How: your occupancy divided by the comp set average occupancy, then multiplied by 100.)">
          <span className={`text-3xl font-extrabold leading-none ${toneStyle.valueClass}`}>
            {opi == null ? "—" : opi.toFixed(1)}
          </span>
        </InfoTip>
        <span className="text-xs text-gray-400 dark:text-gray-500">OPI</span>
      </div>
      <div className="mt-1 text-sm text-gray-600 dark:text-gray-300">{subtitle}</div>
      {underlying && (
        <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 tabular-nums">{underlying}</div>
      )}
    </MetricCardShell>
  );
}

// Format helpers used by Cards 3–5.
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const fmtSyncDate = (iso) => {
  if (!iso) return "";
  const [, month, day] = iso.split("-");
  return `${MONTHS[parseInt(month, 10) - 1]} ${parseInt(day, 10)}`;
};
// CI sync runs daily — day-granular relative time matches the cadence. Hour/minute
// precision implies a freshness we don't actually have.
const fmtSyncAgo = (iso) => {
  if (!iso) return "";
  const timestamp = new Date(iso).getTime();
  if (Number.isNaN(timestamp)) return "";
  const mins = Math.floor((Date.now() - timestamp) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} minute${mins === 1 ? "" : "s"} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "yesterday";
  return `${days} days ago`;
};
const fmtMoney = (value) => (value == null ? "—" : `$${Number(value).toFixed(2)}`);
const fmtSpread = (value) => (value == null ? "—" : `${Number(value).toFixed(1)}×`);

// "rv" → "RV", title-case for the rest. Falls back to "All Categories" when
// no code is passed (used by the category tab bar). Mirrors the shared
// `categoryLabel` in utils/ciFormat — kept local here because the null-code
// fallback differs (this one needs "All Categories", the shared one returns "—").
const CATEGORY_LABELS = { rv: "RV", lodging: "Lodging", tent: "Tent", glamping: "Glamping" };
// Spec tab order: All | RV | Lodging | Tent | Glamping. Unknown codes
// returned by the backend fall to the end in their original order.
const CATEGORY_ORDER = ["rv", "lodging", "tent", "glamping"];
const sortCategories = (codes) => {
  const known = CATEGORY_ORDER.filter((code) => codes.includes(code));
  const unknown = codes.filter((code) => !CATEGORY_ORDER.includes(code));
  return [...known, ...unknown];
};
const categoryLabel = (code) =>
  CATEGORY_LABELS[code] ?? (code ? code.charAt(0).toUpperCase() + code.slice(1) : "All Categories");

const CompetitiveSetCard = ({ state }) => {
  if (state.loading) return <MetricSkeleton />;
  if (state.error) return <MetricError title="Competitive Set" message={state.error} />;

  const cs = state.data?.comp_set || {};
  const direct = cs.direct_count ?? 0;
  const hotels = cs.hotels_count ?? 0;
  const airbnb = cs.airbnb_count ?? 0;

  return (
    <MetricCardShell
      tone="emerald"
      icon="users"
      title="Competitive Set"
      tooltip="Direct Comps are campgrounds and RV parks your team picked to track head-to-head. Area Hotels and Area Airbnbs are all listings within your market radius, used as cross-channel price references."
    >
      <div className="mt-2 flex items-baseline gap-2">
        <span className="text-3xl font-extrabold leading-none text-emerald-600 dark:text-emerald-400">{direct}</span>
        <span className="text-xs text-gray-400 dark:text-gray-500">Direct Comp{direct === 1 ? "" : "s"}</span>
      </div>
      <div className="mt-1.5 text-sm space-y-0.5">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-gray-500 dark:text-gray-400">Area Hotels</span>
          <span className="text-gray-700 dark:text-gray-200 tabular-nums">{hotels}</span>
        </div>
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-gray-500 dark:text-gray-400">Area Airbnbs</span>
          <span className="text-gray-700 dark:text-gray-200 tabular-nums">{airbnb}</span>
        </div>
      </div>
    </MetricCardShell>
  );
};

const RateChangesCard = ({ state, onUnreadClick }) => {
  if (state.loading) return <MetricSkeleton />;
  if (state.error) return <MetricError title="Rate Changes" message={state.error} />;

  const rc = state.data?.rate_changes || {};
  const total = rc.total ?? 0;
  const up = rc.increases ?? 0;
  const down = rc.decreases ?? 0;
  const unread = rc.unread ?? 0;
  // First sync: rate change detection requires comparing two syncs, so trend has ≤1 point.
  // Per spec null-handling, show the wait copy instead of a misleading "0 changes".
  const trendLen = (state.data?.rpi_trend || []).length;
  const isFirstSync = total === 0 && trendLen <= 1;

  if (isFirstSync) {
    return (
      <MetricCardShell tone="brandBlue" icon="trendingUp" title="Rate Changes" tooltip="Count of competitor rate moves (raises and drops) detected since the previous nightly sync. Watch for runs of increases — that's the market signaling rising demand.">
        <div className="mt-2 text-sm text-gray-600 dark:text-gray-300">
          Changes tracked after next sync.
        </div>
      </MetricCardShell>
    );
  }

  return (
    <MetricCardShell
      tone="brandBlue"
      icon="trendingUp"
      title="Rate Changes"
    >
      <div className="mt-2 flex items-baseline gap-2 flex-wrap">
        <span className="text-3xl font-extrabold leading-none text-[#32B1FF]">{total}</span>
        <span className="text-xs text-gray-400 dark:text-gray-500">changes</span>
        {unread > 0 && (
          <button
            type="button"
            onClick={onUnreadClick}
            className="ml-auto inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-500/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 cursor-pointer transition-colors"
            aria-label={`${unread} unread rate changes — jump to Rate Change Feed`}
          >
            {unread} unread
          </button>
        )}
      </div>
      <div className="mt-1 text-sm text-gray-600 dark:text-gray-300">
        <span className="text-emerald-600 dark:text-emerald-400">↑ {up}</span>
        <span className="mx-2 text-gray-400">·</span>
        <span className="text-red-600 dark:text-red-400">↓ {down}</span>
      </div>
    </MetricCardShell>
  );
};

const MarketContextCard = ({ state }) => {
  if (state.loading) return <MetricSkeleton />;
  if (state.error) return <MetricError title="Market Context" message={state.error} />;

  const metrics = state.data?.metrics || {};
  const you = metrics.client_avg_rate;
  const hotel = metrics.hotel_median_rate;
  const airbnb = metrics.airbnb_median_rate;
  const spread = metrics.cross_channel_spread;

  return (
    <MetricCardShell
      tone="brandBlue"
      icon="dollar"
      title="Market Context"
      tooltip="Your average rate alongside the median rates of area hotels and Airbnbs — a quick read on how your pricing compares across channels."
      footer={spread != null ? (
        <span className="inline-flex items-center">
          Spread:&nbsp;
          <InfoTip text="How much more area hotels charge than you. 2.8× means hotels in your area cost nearly 3× what you charge — useful to justify rate increases and in marketing — 'Camp for a third the price of a hotel.' (How: hotel median rate divided by your rate.)">
            <span>{fmtSpread(spread)}</span>
          </InfoTip>
        </span>
      ) : null}
    >
      <div className="mt-2 flex items-baseline gap-2">
        <span className="text-2xl font-extrabold leading-none text-[#32B1FF]">{fmtMoney(you)}</span>
        <span className="text-xs text-gray-400 dark:text-gray-500">You</span>
      </div>
      {(hotel != null || airbnb != null) && (
        <div className="mt-1.5 text-sm space-y-0.5">
          {hotel != null && (
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-gray-500 dark:text-gray-400">Hotels</span>
              <span className="text-gray-700 dark:text-gray-200 tabular-nums">{fmtMoney(hotel)}</span>
            </div>
          )}
          {airbnb != null && (
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-gray-500 dark:text-gray-400">Airbnb</span>
              <span className="text-gray-700 dark:text-gray-200 tabular-nums">{fmtMoney(airbnb)}</span>
            </div>
          )}
        </div>
      )}
    </MetricCardShell>
  );
};

const CompetitiveIntelligencePage = () => {
  usePageMeta({ headerActive: 'competitive-intelligence', sidebarActive: 'competitive' });
  const username = localStorage.getItem("username") || "Admin";
  const view = useView();
  // Multi-Org Membership Epic 1 — role follows the active org (not localStorage).
  const isClient = view.isClient;
  const clientId = view.supabaseClientId;
  // CI is forward-looking (per spec) — global date range doesn't apply. The header's
  // DateRangePicker is disabled (greyed, with a hover hint) on this route via
  // AdminHeader/ClientHeader, and the RPCs below intentionally omit start_date/end_date.

  // Section 1A — summary fetch
  const [summary, setSummary] = useState(initialState);

  // Revenue Opportunity headline for the Analysis subsection takeaway.
  // We fetch it at the page level (independent of the accordion expansion) so the
  // closed-state takeaway reads the same total as the expanded panel. The panel
  // inside Analysis → Revenue Opportunity → Revenue vs Market also fetches this
  // RPC via AnalysisPanel — slight duplication, but the panel is lazy-mounted
  // inside the accordion, so we can't easily share that result up.
  const [revenueOpp, setRevenueOpp] = useState(initialState);

  // B1 — page_verdict hero sentence (from ci_get_actions). null until a verdict
  // is found; stays null on empty/error so the hero simply doesn't render.
  const [pageVerdict, setPageVerdict] = useState(null);

  // B2/B4 — panel insights (ci_get_panel_insights), the per-panel sentence layer
  // that powers `say`. Fetched on tab change so the verdict + per-panel takeaways
  // re-scope with the active category. The page uses independent useEffects (no
  // Promise.all); this is the 6th fetch alongside summary/filters/revenueOpp/
  // verdict/calendar.
  const [insights, setInsights] = useState(initialState);

  // B5/B6 — the `say` map passed to every analysis panel + VerdictBlock. Built from
  // the insights response (see the builder effect below). null until insights
  // resolve with at least one surviving sentence.
  const [say, setSay] = useState(null);

  // Item 3 — selected absorption date (ci_get_absorption_dates). Wired to
  // AbsorptionDatePicker in a later session; the say builder matches absorption
  // views' `check_in` against it. null until the user picks a date.
  const [selectedAbsorptionDate, setSelectedAbsorptionDate] = useState(null);

  // Section 2 — category tabs + site type dropdown
  // selectedCategory === null  → "All categories" (pass null as p_category_code)
  // selectedGroup    === null  → "All site types"
  // selectedTier     === null  → "All tiers" (no UI yet; threaded through to Section 3+ RPCs)
  // Category tab + site type are URL-backed for shareable links (mirrors the
  // RouteIntelligencePage / EventIntelligencePage updateParam pattern). Section 8
  // panel selections and the rate drawer stay local (low share-value, would
  // clutter the URL). `category` empty → "All categories"; `group` → site type.
  const [searchParams, setSearchParams] = useSearchParams();
  const updateParam = useCallback((key, value) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value == null || value === "") next.delete(key);
      else next.set(key, String(value));
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const [filters, setFilters] = useState(initialState);
  const [selectedCategory, setSelectedCategoryState] = useState(() => searchParams.get("category") || null);
  const [selectedGroup, setSelectedGroupState] = useState(() => searchParams.get("group") || null);
  const [selectedTier, setSelectedTier] = useState(null);

  // Item 7 (ci-ui-cleanup §C7) — per-category usability probe. ci_get_available_filters
  // surfaces categories based on competitor data, not client inventory, so the tab bar
  // can render tabs the client has no usable data for. We probe the rate calendar once
  // per (client, category-set) and record which categories have at least one row with a
  // non-null client_rate; CategoryTabsSection hides the rest. See the probe effect
  // declared just after `availableCategories` for the fetch + the usability rule.
  const [categoryProbe, setCategoryProbe] = useState({ loading: false, usable: {} });

  // Setters keep React state and the URL in sync.
  const setSelectedCategory = useCallback((cat) => {
    setSelectedCategoryState(cat);
    updateParam("category", cat || "");
  }, [updateParam]);
  const setSelectedGroup = useCallback((group) => {
    setSelectedGroupState(group);
    updateParam("group", group || "");
  }, [updateParam]);

  // Seed with the initial (URL-restored) category so the site-type reset effect's
  // first run is a no-op — robust to StrictMode double-invoke, and preserves a
  // `group` restored from the URL until the category actually changes.
  const prevCategoryRef = useRef(selectedCategory);

  // Section 3A — 90-day rate calendar.
  // Pattern B (matches loadWastedSpend / loadSearchTerms in GoogleAdsPage):
  // fetch the first server page, then parallel-fetch the rest. Once everything is
  // in memory, the panel paginates client-side. The dataset is bounded (~90 days
  // forward, ~100 rows max), so this is cheap and removes the per-click reload flash.
  const RATE_CALENDAR_FETCH_LIMIT = 100;
  const [rateCalendar, setRateCalendar] = useState(initialState);

  // Section 3B — rate detail drawer (opens on row click).
  const [drawerRow, setDrawerRow] = useState(null);

  // Section 8 — active panel within each analysis subsection.
  const [activeAnalysisPanels, setActiveAnalysisPanels] = useState({
    pricing: "price-gaps",
    demand: "demand-signals",
    revenue: "revenue-opportunity",
    competitor: "availability-trends",
    absorption: "absorption-velocity",
  });
  const setActiveAnalysisPanel = (group, panelKey) =>
    setActiveAnalysisPanels((prev) => ({ ...prev, [group]: panelKey }));

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    setSummary({ loading: true, error: "", data: null });
    getCiSummary(clientId)
      .then((resp) => {
        if (!cancelled) setSummary({ loading: false, error: "", data: resp?.data ?? null });
      })
      .catch((err) => {
        if (!cancelled) setSummary({ loading: false, error: err?.message || "Failed to load", data: null });
      });
    return () => { cancelled = true; };
  }, [clientId]);
  // Item 1 (work-page-fixes) — refresh the summary without clearing existing
  // data so the Rate Changes KPI's unread badge updates live after the user
  // marks rate changes as read in the feed. Unlike the initial-load effect
  // above (which clears to null → skeleton), this keeps the prior data visible
  // during refetch to avoid a layout flash, and preserves the last good data
  // on a transient error.
  const refetchSummary = useCallback(() => {
    if (!clientId) return;
    getCiSummary(clientId)
      .then((resp) => setSummary({ loading: false, error: "", data: resp?.data ?? null }))
      .catch((err) => setSummary((prev) => ({ loading: false, error: err?.message || "Failed to load", data: prev.data })));
  }, [clientId]);

  // Item 1 (work-page-fixes) — smooth-scroll to the Rate Change Feed section.
  // The feed wrapper below carries id="rate-change-feed" (mirrors the existing
  // ai-pricing-actions anchor pattern with scroll-mt-20 for the sticky header).
  const scrollToRateFeed = useCallback(() => {
    document.getElementById("rate-change-feed")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);



  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    setFilters({ loading: true, error: "", data: null });
    getCiAvailableFilters(clientId)
      .then((resp) => {
        if (!cancelled) setFilters({ loading: false, error: "", data: resp?.data ?? null });
      })
      .catch((err) => {
        if (!cancelled) setFilters({ loading: false, error: err?.message || "Failed to load filters", data: null });
      });
    return () => { cancelled = true; };
  }, [clientId]);

  // Reset site type only when the category tab actually CHANGES from its previous
  // value — comparing against a ref (not a "skip first run" flag) is robust to
  // StrictMode double-invoke and preserves a `group` restored from the URL.
  useEffect(() => {
    if (prevCategoryRef.current !== selectedCategory) {
      prevCategoryRef.current = selectedCategory;
      setSelectedGroup(null);
    }
  }, [selectedCategory, setSelectedGroup]);

  // Revenue Opportunity fetch for the Analysis takeaway. Re-runs on category
  // change so the takeaway re-scopes when the user switches tabs. Defaults to
  // "rv" when no category is selected, matching the panel's own default.
  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    setRevenueOpp((prev) => ({ loading: true, error: "", data: prev.data }));
    getCiReportRevenueOpportunity(clientId, { categoryCode: selectedCategory || "rv" })
      .then((resp) => {
        if (cancelled) return;
        const payload = resp?.data ?? resp ?? [];
        setRevenueOpp({ loading: false, error: "", data: Array.isArray(payload) ? payload : [] });
      })
      .catch(() => {
        if (cancelled) return;
        setRevenueOpp({ loading: false, error: "", data: [] });
      });
    return () => { cancelled = true; };
  }, [clientId, selectedCategory]);

  // B1 — page_verdict hero. Extracted from ci_get_actions: the verdict lives on
  // the top-priority action's supporting_data.page_verdict. Actions are generated
  // once daily by cron (C2) — an empty response means generation hasn't run yet,
  // not a failure, so we silently leave the hero absent. Read-RPC errors (C4) are
  // Postgres-level and likewise swallowed (no placeholder, no error state).
  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    getCiActions(clientId)
      .then((resp) => {
        if (cancelled) return;
        const actions = Array.isArray(resp?.data) ? resp.data : [];
        const verdict = actions
          .map((a) => a.supporting_data?.page_verdict)
          .find(Boolean) ?? null;
        setPageVerdict(verdict);
      })
      .catch(() => {
        if (!cancelled) setPageVerdict(null);
      });
    return () => { cancelled = true; };
  }, [clientId]);

  // Section 3A fetch — re-runs on category/tier change only. The Site Type
  // filter is applied client-side over the fetched dataset (see the panel),
  // because the backend's `normalized_group` column has data-quality issues
  // we don't want to inherit. Switching site type therefore doesn't refetch.
  // Rate Calendar shows the fixed next-90-day window — intentionally NOT scoped
  // by the navbar date range.
  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    setRateCalendar({ loading: true, error: "", data: null });
    const baseParams = {
      categoryCode: selectedCategory,
      tier: selectedTier,
      limit: RATE_CALENDAR_FETCH_LIMIT,
    };
    // Keep page 1's envelope so its non-item fields survive on the merged result;
    // fetchAllPages runs the shared paging loop.
    let firstData = {};
    fetchAllPages(async (page) => {
      const data = (await getCiRateCalendar(clientId, { ...baseParams, page }))?.data ?? {};
      if (page === 1) firstData = data;
      return data;
    })
      .then((allItems) => {
        if (!cancelled) {
          const merged = [...allItems];
          merged.sort((rowA, rowB) => (rowA.check_in || "").localeCompare(rowB.check_in || ""));
          setRateCalendar({ loading: false, error: "", data: { ...firstData, items: merged } });
        }
      })
      .catch((err) => {
        if (!cancelled) setRateCalendar({ loading: false, error: err?.message || "Failed to load rate calendar", data: null });
      });
    return () => { cancelled = true; };
  }, [clientId, selectedCategory, selectedTier]);

  // Memoized because the validation effect below depends on this array. A bare
  // `filters.data?.categories || []` produced a NEW [] on every render whenever the
  // payload lacked `.categories`, so the effect re-ran continuously and cleared an
  // already-resolved category (which re-triggered the probe fetches downstream).
  const availableCategories = useMemo(() => filters.data?.categories || [], [filters.data]);

  // Lightweight usability probe. ci_get_available_filters now returns only categories
  // the client sells, but the rate calendar for a sold category can still come back
  // empty — e.g. Brian's lodging-data fix may not be deployed yet, so every lodging
  // row arrives with a null client_rate. Rather than make a separate fetch we reuse the
  // rate-calendar state the page already loads: a category is usable iff ≥1 calendar
  // row carries a non-null client_rate for its category_code. Only the All-tab fetch
  // (selectedCategory === null) returns every category's rows, so we gate on that — a
  // category-specific fetch would return just one category and wipe the rest. On error
  // we fall back to showing every category so a transient blip doesn't hide a tab that
  // may have data. The validation effect below still clears a stale ?category= URL
  // param for a category the client doesn't sell (gated on availableCategories).
  useEffect(() => {
    if (!availableCategories.length) return;
    if (selectedCategory) return;
    if (rateCalendar.loading) {
      setCategoryProbe({ loading: true, usable: {} });
      return;
    }
    if (rateCalendar.error) {
      const usable = {};
      for (const code of availableCategories) usable[code] = true;
      setCategoryProbe({ loading: false, usable });
      return;
    }
    if (!rateCalendar.data) {
      setCategoryProbe({ loading: true, usable: {} });
      return;
    }
    const items = rateCalendar.data?.items;
    const usable = {};
    for (const code of availableCategories) usable[code] = false;
    if (Array.isArray(items)) {
      for (const row of items) {
        if (row.category_code && row.client_rate != null) {
          usable[row.category_code] = true;
        }
      }
    }
    setCategoryProbe({ loading: false, usable });
  }, [availableCategories, rateCalendar, selectedCategory]);

  // Categories that survived the usability probe (≥1 row with a non-null client_rate).
  // Empty while the probe is in flight — the tab bar shows a skeleton over that window
  // (see the combined `loading` passed to CategoryTabsSection) so unusable tabs never
  // flash in and back out.
  const usableCategories = useMemo(
    () => availableCategories.filter((code) => categoryProbe.usable[code] === true),
    [availableCategories, categoryProbe.usable]
  );

  // Normalized site-type names per category, from ci_get_available_filters
  // (e.g. rv → {Full Hookup, Pull-Through, Back-In}; lodging → {Airbnb, Hotel}).
  // Used to scope the Inventory Concentration report client-side: its RPC has no
  // category param and returns a single client-wide payload keyed by these same
  // normalized names, so we can filter its rows to the selected category rather
  // than show mismatched data. (See "Competitive Intelligence — Backend Gaps.md".)
  const categoryGroupNames = useMemo(() => {
    if (!selectedCategory) return null; // null → "All": show the full client-wide report
    const groups = filters.data?.normalized_groups;
    if (!Array.isArray(groups)) return [];
    return groups
      .filter((group) => group.category_code === selectedCategory)
      .map((group) => group.normalized_group);
  }, [filters.data, selectedCategory]);

  // Item 3 — per-category details (properties_by_tier) for the active tab, sliced
  // from ci_get_available_filters' `category_details`. Passed to RateCalendarPanel
  // and MarketPositionPanel for their GroupSelect (competitor group / p_tier).
  // null under "All categories" (no single category to scope) — GroupSelect
  // self-hides in that case.
  const categoryDetails = useMemo(
    () => (selectedCategory ? filters.data?.category_details?.[selectedCategory] : null),
    [filters.data, selectedCategory]
  );

  // Site-type options derive from the actual rows shown in the Rate Calendar
  // dataset for the active category, using raw `client_site_type_name` values
  // (the property's own labels, e.g. "Deluxe Pull Thru 50 AMP / 30 AMP FHU").
  // Filtering happens client-side in both panels; we don't send the param to
  // the backend because its `normalized_group` mapping has data-quality gaps.
  // Sourcing from displayed rows guarantees every option has data backing it.
  const availableGroups = useMemo(() => {
    if (!selectedCategory) return [];
    const items = rateCalendar.data?.items;
    if (!Array.isArray(items)) return [];
    const counts = new Map();
    for (const row of items) {
      if (row.category_code !== selectedCategory) continue;
      const name = row.client_site_type_name;
      if (!name) continue;
      counts.set(name, (counts.get(name) || 0) + 1);
    }
    return Array.from(counts.entries())
      .sort(([nameA], [nameB]) => nameA.localeCompare(nameB))
      .map(([name, count]) => ({
        normalized_group: name,
        category_code: selectedCategory,
        date_windows: count,
      }));
  }, [rateCalendar.data, selectedCategory]);

  // Validate a URL-restored category against the loaded filters. If a shared link
  // (or a client switch that remounts the page) carries a category the current
  // client doesn't have, fall back to "All" so we don't render an empty filtered
  // view. We reset STATE only and leave the raw param in the URL untouched —
  // matching the house convention (RouteIntelligencePage validates `tab` at read
  // time via a switch-default and never scrubs an unknown value from the URL).
  useEffect(() => {
    if (filters.loading || !filters.data) return;
    if (selectedCategory && !availableCategories.includes(selectedCategory)) {
      setSelectedCategoryState(null);
      setSelectedGroupState(null);
    }
  }, [filters.loading, filters.data, availableCategories, selectedCategory]);

  useEffect(() => {
    // Drop a restored site type that isn't a real option for the active category
    // (state only — same read-time-tolerance convention as above).
    if (!selectedGroup || availableGroups.length === 0) return;
    const ok = availableGroups.some((group) => group.normalized_group === selectedGroup);
    if (!ok) setSelectedGroupState(null);
  }, [selectedGroup, availableGroups]);

  // Item 7 — if the selected category turns out to have no usable client data (its tab
  // is hidden), fall back to "All" so we never render an empty category-scoped view with
  // no visible active tab. State-only reset (URL param left untouched — same house
  // convention as the availableCategories validation above). Gated on probe completion
  // so we don't reset during the in-flight window when usableCategories is still empty.
  useEffect(() => {
    if (categoryProbe.loading) return;
    if (!selectedCategory) return;
    if (usableCategories.includes(selectedCategory)) return;
    setSelectedCategoryState(null);
    setSelectedGroupState(null);
  }, [categoryProbe.loading, usableCategories, selectedCategory]);

  // B2/B3 — panel insights fetch (ci_get_panel_insights). Re-runs on tab change so the
  // `say` map (below) and VerdictBlock reflect the active category. Independent useEffect
  // — the page doesn't use Promise.all; this is the 6th fetch.
  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    setInsights({ loading: true, error: "", data: null });
    ciGetPanelInsights(clientId, selectedCategory)
      .then((resp) => {
        if (cancelled) return;
        setInsights({ loading: false, error: "", data: resp?.data ?? null });
      })
      .catch((err) => {
        if (cancelled) return;
        setInsights({ loading: false, error: err?.message || "Failed to load insights", data: null });
      });
    return () => { cancelled = true; };
  }, [clientId, selectedCategory]);

  // B5/B6 — build the `say` map from the insights response once it resolves. Each entry
  // in `insights.data.panels` carries a `panel_key`, a `view` ({ category_code, check_in,
  // confidence }), and a `sentence`. We fold in only the views that match the active tab
  // (category_code === selectedCategory) and — for absorption panels — the selected
  // absorption date (check_in === selectedAbsorptionDate), excluding confidence ===
  // "insufficient". The top-level `verdict` sentence (non-null) goes on `say.verdict`,
  // which VerdictBlock renders above the panels. null when nothing survives the filters
  // so consumers render nothing rather than an empty object. While no absorption date is
  // picked yet (Item 3 wires the picker), absorption views are kept so their takeaways
  // aren't blank in the interim.
  useEffect(() => {
    if (insights.loading || !insights.data) { setSay(null); return; }
    const panels = Array.isArray(insights.data?.panels) ? insights.data.panels : [];
    const next = {};
    for (const entry of panels) {
      const view = entry?.view;
      if (!view) continue;
      if (view.confidence === "insufficient") continue;
      if (view.category_code && view.category_code !== selectedCategory) continue;
      if (view.check_in && selectedAbsorptionDate != null && view.check_in !== selectedAbsorptionDate) continue;
      if (entry.panel_key && entry.sentence) next[entry.panel_key] = entry.sentence;
    }
    const verdict = insights.data?.verdict;
    if (verdict) next.verdict = verdict;
    setSay(Object.keys(next).length > 0 ? next : null);
  }, [insights, selectedCategory, selectedAbsorptionDate]);

  const hasNoData = summary.data && summary.data.has_data === false;

  // Apply the page-level Site Type filter to Rate Calendar rows client-side,
  // matching against the raw `client_site_type_name` (same vocabulary used by
  // the dropdown). The fetched dataset itself is not refetched on group change.
  // Declared above analysisTakeaways so the takeaway counts can source from the
  // same filtered set the Rate Calendar panel renders.
  const rateCalendarFiltered = useMemo(() => {
    const items = rateCalendar.data?.items;
    if (!Array.isArray(items)) return rateCalendar;
    // B1 — drop rows from categories where Verde has no inventory (e.g. tent):
    // both client_rate and client_site_type_name are null on these rows. Only
    // the "All" tab (selectedCategory === null, p_category_code: null) returns
    // mixed-category rows that include them, so we filter only in that case.
    // Category-specific tabs are left untouched: the RV tab already returns
    // clean rows (no nulls), and the Tent tab intentionally returns tent rows
    // the user asked to see — filtering them there would wipe the whole tab.
    // We keep rows where Verde has a rate even if comp data is null — only
    // true no-inventory rows (both fields null) are removed.
    const baseItems = !selectedCategory
      ? items.filter((row) => !(row.client_rate === null && row.client_site_type_name === null))
      : items;
    const filteredItems = selectedGroup
      ? baseItems.filter((row) => row.client_site_type_name === selectedGroup)
      : baseItems;
    return { ...rateCalendar, data: { ...rateCalendar.data, items: filteredItems } };
  }, [rateCalendar, selectedCategory, selectedGroup]);

  // One-line takeaways for each Analysis subsection's collapsed header.
  // Audit recommendation #3.1: today the closed state says nothing, so users have
  // to expand each section to know whether it's worth opening. We derive these
  // from data the page already fetched (summary + rate calendar) — no new RPC
  // calls — and pass them down to each AnalysisSubsection's header.
  const analysisTakeaways = useMemo(() => {
    // Source from the *filtered* dataset so the Demand & Timing counts always
    // match what the Rate Calendar panel actually renders. Earlier this read
    // from the unfiltered set, so picking a site type made the takeaway drift
    // from the table beneath it.
    const items = Array.isArray(rateCalendarFiltered.data?.items) ? rateCalendarFiltered.data.items : [];
    const metrics = summary.data?.metrics;
    const compSet = summary.data?.comp_set;
    const rateChanges = summary.data?.rate_changes;
    const rpiTrend = summary.data?.rpi_trend;

    // Pricing & Position: how many dates are priced above market, and by how much on average.
    let pricing = null;
    const pricedRows = items.filter((row) => row.gap_pct != null);
    if (pricedRows.length > 0) {
      const premium = pricedRows.filter((row) => row.gap_pct > 5);
      const avgGap = pricedRows.reduce((acc, row) => acc + row.gap_pct, 0) / pricedRows.length;
      pricing = premium.length > 0
        ? `${premium.length} of ${pricedRows.length} dates premium-positioned · avg ${avgGap > 0 ? "+" : ""}${avgGap.toFixed(1)}% vs market`
        : `${pricedRows.length} dates priced · avg ${avgGap > 0 ? "+" : ""}${avgGap.toFixed(1)}% vs market`;
    }

    // Demand & Timing: count of weekend + holiday + weekday-sample windows in the
    // next 90 days. We also sweep up any rows whose `date_window_type` isn't one
    // of the three known buckets into an "other" bucket — keeps the takeaway's
    // parts in sum agreement with the Rate Calendar total below it, even if the
    // backend ever returns a new window type the UI hasn't been updated for.
    let demand = null;
    if (items.length > 0) {
      let weekends = 0, holidays = 0, weekdays = 0, other = 0;
      for (const row of items) {
        switch (row.date_window_type) {
          case "weekend": weekends++; break;
          case "holiday": holidays++; break;
          case "weekday_sample": weekdays++; break;
          default: other++;
        }
      }
      const parts = [];
      if (weekends) parts.push(`${weekends} weekend${weekends === 1 ? "" : "s"}`);
      if (holidays) parts.push(`${holidays} holiday${holidays === 1 ? "" : "s"}`);
      if (weekdays) parts.push(`${weekdays} weekday sample${weekdays === 1 ? "" : "s"}`);
      if (other) parts.push(`${other} other`);
      demand = parts.length > 0 ? `${parts.join(" · ")} in the next 90 days` : null;
    }

    // Revenue Opportunity: projected revenue delta from ci_report_revenue_opportunity.
    // We deliberately use the same RPC the expanded panel uses (rather than deriving
    // from rate calendar rows) so the takeaway and the panel total agree to the cent.
    // Backend convention: positive `revenue_opportunity` = below market (room to raise);
    // we negate at the UI so positive = above market (premium positioning, green).
    let revenue = null;
    const revenueRows = Array.isArray(revenueOpp.data) ? revenueOpp.data : [];
    if (revenueRows.length > 0) {
      const total = revenueRows.reduce((acc, row) => acc + (row.revenue_opportunity == null ? 0 : -row.revenue_opportunity), 0);
      const totalDates = revenueRows.reduce((acc, row) => acc + (row.dates ?? 0), 0);
      const sign = total >= 0 ? "+" : "−";
      const label = total >= 0 ? "above market" : "below market";
      revenue = `${sign}$${Math.abs(total).toLocaleString(undefined, { maximumFractionDigits: 0 })} ${label} across ${totalDates} date${totalDates === 1 ? "" : "s"}`;
    }

    // Competitor Intel: comp set composition + rate change activity since last sync.
    // "Airbnb" stays capitalized (brand name); other category labels are lowercase
    // to match the house style for descriptive helper copy.
    let competitor = null;
    if (compSet) {
      const parts = [];
      if (compSet.direct_count) parts.push(`${compSet.direct_count} direct`);
      if (compSet.hotels_count) parts.push(`${compSet.hotels_count} hotel${compSet.hotels_count === 1 ? "" : "s"}`);
      if (compSet.airbnb_count) parts.push(`${compSet.airbnb_count} Airbnb`);
      if (parts.length) {
        const changeTotal = rateChanges?.total ?? 0;
        const changeSuffix = changeTotal > 0
          ? ` · ${changeTotal} rate change${changeTotal === 1 ? "" : "s"} detected`
          : " · awaiting next sync for rate changes";
        competitor = parts.join(" · ") + changeSuffix;
      }
    }

    // Absorption & Trends: how many syncs have landed so far. Most reports here
    // need 2+ syncs to be meaningful, so just say where we are in the buildup.
    let absorption = null;
    const syncCount = Array.isArray(rpiTrend) ? rpiTrend.length : 0;
    if (syncCount > 0) {
      absorption = syncCount === 1
        ? "1 sync so far · more reports unlock after the next sync"
        : `${syncCount} syncs collected · absorption + trend reports available`;
    } else if (metrics) {
      absorption = "Awaiting first sync — reports unlock once data lands";
    }

    return { pricing, demand, revenue, competitor, absorption };
  }, [rateCalendarFiltered.data, summary.data, revenueOpp.data]);

  return (
    <>
        <main className="ci-scope flex-1 min-w-0">
          <div className="px-4 md:px-6 lg:px-10 py-6 space-y-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Competitive Intelligence</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Track your rate position, occupancy, and competitive set against the local market.
                </p>
              </div>
              {summary.data?.config?.last_sync_at && (
                <span
                  className="text-[11px] text-gray-400 dark:text-gray-500 cursor-help"
                  title={`${new Date(summary.data.config.last_sync_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} at ${new Date(summary.data.config.last_sync_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`}
                >
                  Last synced: {fmtSyncAgo(summary.data.config.last_sync_at)}
                </span>
              )}
            </div>

            {/* Top insight verdict — derived from RPI × OPI quadrant; AI-first narrative with deterministic fallback.
                B1: page_verdict (from ci_get_actions) is passed down and rendered as a
                headline summary inside VerdictCard when non-null. */}
            {!hasNoData && summary.data?.metrics && (
              <VerdictCard
                summary={summary}
                clientId={clientId}
                pageVerdict={pageVerdict}
              />
            )}

            {/* SECTION 1A — Summary KPI Cards */}
            {hasNoData ? (
              <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-8 text-center text-sm text-gray-500 dark:text-gray-400">
                No competitive data yet. Data syncs daily.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                <RpiCard state={summary} />
                <OpiCard state={summary} />
                <CompetitiveSetCard state={summary} />
                <RateChangesCard state={summary} onUnreadClick={scrollToRateFeed} />
                <MarketContextCard state={summary} />
              </div>
            )}

            {/* SECTION 2 — Category Filter (drives all category-aware sections below).
                Sticky so the filter stays reachable as the user scrolls through downstream
                panels (Rate Change Feed, Analysis, etc.). The `-mx`/`px` pair lets the
                sticky bar span the page gutters so the backdrop covers content sliding
                underneath. `top-0` pins to viewport since the page header is not fixed. */}
            {!hasNoData && (
              <div className="sticky top-0 z-30 -mx-4 md:-mx-6 lg:-mx-10 px-4 md:px-6 lg:px-10 py-2 bg-[#f7f8fa]/95 dark:bg-gray-950/95 backdrop-blur supports-[backdrop-filter]:bg-[#f7f8fa]/80 dark:supports-[backdrop-filter]:bg-gray-950/80 border-b border-transparent">
                <CategoryTabsSection
                  state={{ loading: filters.loading || categoryProbe.loading, error: filters.error }}
                  categories={availableCategories}
                  usableCategories={usableCategories}
                  selectedCategory={selectedCategory}
                  onSelectCategory={setSelectedCategory}
                />
              </div>
            )}

            {/* SECTION 3A — Rate Trend Lines */}
            {!hasNoData && (
              <RateTrendsPanel clientId={clientId} categoryCode={selectedCategory} />
            )}

            {/* SECTION 3B — 90-Day Rate Calendar (owns its Site type filter) */}
            {!hasNoData && (
              <RateCalendarPanel
                state={rateCalendarFiltered}
                onRowClick={(row) => setDrawerRow(row)}
                showSiteType={!!selectedCategory}
                siteType={selectedGroup}
                siteTypeOptions={availableGroups}
                onSiteTypeChange={setSelectedGroup}
                siteTypeDisabled={rateCalendar.loading || availableGroups.length === 0}
                categoryDetails={categoryDetails}
                onTierChange={setSelectedTier}
              />
            )}

            {/* SECTIONS 4 + 5 — side-by-side per spec wireframe */}
            {!hasNoData && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
                <MarketPositionPanel
                  clientId={clientId}
                  categoryCode={selectedCategory}
                  availableCategories={availableCategories}
                  categoryDetails={categoryDetails}
                />
                <div id="rate-change-feed" className="scroll-mt-20">
                  <RateChangeFeedPanel
                    clientId={clientId}
                    categoryCode={selectedCategory}
                    onMarkRead={refetchSummary}
                  />
                </div>
              </div>
            )}

            {/* SECTION 6 — AI Pricing Actions */}
            {!hasNoData && (
              <div id="ai-pricing-actions" className="scroll-mt-20">
                <AiPricingActionsPanel clientId={clientId} />
              </div>
            )}

            {/* SECTION 7 — Competitor Profiles */}
            {!hasNoData && (
              <CompetitorProfilesPanel clientId={clientId} />
            )}

            {/* SECTION 8 — Analysis Panels (grouped, collapsible, lazy-loaded) */}
            {!hasNoData && (
              <div className="space-y-3">
                <div>
                  <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Analysis</h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Expand a subsection to explore its reports. Category-scoped reports follow the tab selection above.
                  </p>
                </div>

                {/* B8 — verdict sentence above the analysis panels. VerdictBlock returns
                    null when say.verdict is absent, so the block is hidden entirely when
                    there is no verdict (no gap, no placeholder). */}
                <VerdictBlock panelKey="verdict" say={say} />

                <AnalysisSubsection
                  title="Pricing & Position"
                  takeaway={analysisTakeaways.pricing}
                  active={activeAnalysisPanels.pricing}
                  onChange={(panelKey) => setActiveAnalysisPanel("pricing", panelKey)}
                  tabs={[
                    { key: "price-gaps", label: "Price Gaps", node: <PriceGapsPanel clientId={clientId} categoryCode={selectedCategory} say={say} /> },
                    { key: "rate-integrity", label: "Rate Integrity", node: <RateIntegrityPanel clientId={clientId} categoryCode={selectedCategory} say={say} /> },
                    { key: "competitive-ranking", label: "Competitive Ranking", node: <CompetitiveRankingPanel clientId={clientId} categoryCode={selectedCategory} say={say} /> },
                    { key: "site-comparison", label: "Site Comparison", node: <SiteComparisonPanel clientId={clientId} categoryCode={selectedCategory} say={say} /> },
                    { key: "market-position", label: "Market Position Detail", node: <MarketPositionDetailPanel clientId={clientId} categoryCode={selectedCategory} say={say} /> },
                  ]}
                />

                <AnalysisSubsection
                  title="Demand & Timing"
                  takeaway={analysisTakeaways.demand}
                  active={activeAnalysisPanels.demand}
                  onChange={(panelKey) => setActiveAnalysisPanel("demand", panelKey)}
                  tabs={[
                    { key: "demand-signals", label: "Demand Signals", node: <DemandSignalsPanel clientId={clientId} say={say} /> },
                    { key: "event-spikes", label: "Event Spikes", node: <EventSpikesPanel clientId={clientId} categoryCode={selectedCategory} say={say} /> },
                    { key: "high-value-dates", label: "High-Value Dates", node: <HighValueDatesPanel clientId={clientId} say={say} /> },
                    { key: "booking-window", label: "Booking Window", node: <BookingWindowPanel clientId={clientId} categoryCode={selectedCategory} say={say} /> },
                    { key: "forward-calendar", label: "Forward Calendar", node: <ForwardCalendarPanel clientId={clientId} categoryCode={selectedCategory} say={say} /> },
                    { key: "weekday-weekend", label: "Weekday vs Weekend", node: <WeekdayWeekendPanel clientId={clientId} categoryCode={selectedCategory} say={say} /> },
                  ]}
                />

                <AnalysisSubsection
                  title="Revenue Opportunity"
                  takeaway={analysisTakeaways.revenue}
                  active={activeAnalysisPanels.revenue}
                  onChange={(panelKey) => setActiveAnalysisPanel("revenue", panelKey)}
                  tabs={[
                    { key: "revenue-opportunity", label: "Revenue vs Market", node: <RevenueOpportunityPanel clientId={clientId} categoryCode={selectedCategory} say={say} /> },
                    { key: "dynamic-pricing", label: "Dynamic Pricing", node: <DynamicPricingPanel clientId={clientId} categoryCode={selectedCategory} say={say} /> },
                    { key: "inventory-concentration", label: "Inventory Concentration", node: <InventoryConcentrationPanel clientId={clientId} categoryCode={selectedCategory} categoryGroupNames={categoryGroupNames} say={say} /> },
                    { key: "dow-revenue-heatmap", label: "DoW Revenue Heatmap", node: <DowRevenueHeatmapPanel clientId={clientId} categoryCode={selectedCategory} say={say} /> },
                  ]}
                />

                <AnalysisSubsection
                  title="Competitor Intel"
                  takeaway={analysisTakeaways.competitor}
                  active={activeAnalysisPanels.competitor}
                  onChange={(panelKey) => setActiveAnalysisPanel("competitor", panelKey)}
                  tabs={[
                    { key: "availability-trends", label: "Availability Trends", node: <AvailabilityTrendsPanel clientId={clientId} say={say} /> },
                    { key: "competitor-vulnerability", label: "Competitor Vulnerability", node: <CompetitorVulnerabilityPanel clientId={clientId} say={say} /> },
                    { key: "price-distribution", label: "Price Distribution", node: <PriceDistributionPanel clientId={clientId} categoryCode={selectedCategory} say={say} /> },
                    { key: "dow-heatmap", label: "DoW Heatmap", node: <DowHeatmapPanel clientId={clientId} categoryCode={selectedCategory} say={say} /> },
                  ]}
                />

                <AnalysisSubsection
                  title="Absorption & Trends"
                  takeaway={analysisTakeaways.absorption}
                  active={activeAnalysisPanels.absorption}
                  onChange={(panelKey) => setActiveAnalysisPanel("absorption", panelKey)}
                  tabs={[
                    { key: "absorption-velocity", label: "Absorption Velocity", node: <AbsorptionVelocityPanel clientId={clientId} categoryCode={selectedCategory} say={say} /> },
                    { key: "absorption-baseline", label: "Absorption vs Baseline", node: <AbsorptionVsBaselinePanel clientId={clientId} categoryCode={selectedCategory} say={say} /> },
                    { key: "absorption-history", label: "Absorption History", node: <AbsorptionHistoryPanel clientId={clientId} categoryCode={selectedCategory} say={say} onDateChange={setSelectedAbsorptionDate} /> },
                    { key: "market-absorption", label: "Market Absorption", node: <MarketAbsorptionPanel clientId={clientId} categoryCode={selectedCategory} say={say} onDateChange={setSelectedAbsorptionDate} /> },
                    { key: "demand-index", label: "Demand Index", node: <DemandIndexPanel clientId={clientId} categoryCode={selectedCategory} say={say} /> },
                  ]}
                />
              </div>
            )}

            {/* SECTION 10 — Holiday Premiums */}
            {!hasNoData && (
              <HolidayPremiumsPanel clientId={clientId} categoryCode={selectedCategory} />
            )}
          </div>
        </main>

      {/* SECTION 3B — Rate Detail Drawer */}
      <RateDetailDrawer
        open={!!drawerRow}
        onClose={() => setDrawerRow(null)}
        clientId={clientId}
        checkIn={drawerRow?.check_in}
        categoryCode={drawerRow?.category_code ?? selectedCategory}
        dateWindowName={drawerRow?.date_window_name}
      />
    </>
  );
};

// Each Analysis subsection is collapsed by default — the audit flagged the
// hub as 2,918 px tall with 5 sub-modules; collapsing keeps the page short
// and lets users open only what they need. Tab state is preserved across
// open/close (lifted to the parent), so re-opening a section restores the
// previously selected sub-tab. Body is unmounted while collapsed so its
// data fetches are deferred until the user expands.
// Icons match the visual language of Google Ads' Dimensional Breakdowns
// header rows: a single brand-blue 20px stroked icon next to the title.
const SUBSECTION_ICONS = {
  "Pricing & Position": (
    <svg className="w-5 h-5 text-[#32B1FF]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  ),
  "Demand & Timing": (
    <svg className="w-5 h-5 text-[#32B1FF]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
    </svg>
  ),
  "Revenue Opportunity": (
    <svg className="w-5 h-5 text-[#32B1FF]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" />
    </svg>
  ),
  "Competitor Intel": (
    <svg className="w-5 h-5 text-[#32B1FF]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  "Absorption & Trends": (
    <svg className="w-5 h-5 text-[#32B1FF]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <polyline points="3 17 9 11 13 15 21 7" /><polyline points="14 7 21 7 21 14" />
    </svg>
  ),
};

// Each Analysis subsection is collapsed by default — the audit flagged the
// hub as 2,918 px tall with 5 sub-modules; collapsing keeps the page short
// and lets users open only what they need. Tab state is preserved across
// open/close (lifted to the parent), so re-opening a section restores the
// previously selected sub-tab. Body is unmounted while collapsed so its
// data fetches are deferred until the user expands.
const AnalysisSubsection = ({ title, tabs, active, onChange, takeaway }) => {
  const [open, setOpen] = useState(false);
  const activeTab = tabs.find((tab) => tab.key === active) ?? tabs[0];
  const icon = SUBSECTION_ICONS[title] ?? null;
  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-3 px-4 sm:px-5 py-3 sm:py-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors text-left"
      >
        <div className="flex items-center gap-2 min-w-0">
          {icon}
          <div className="min-w-0">
            <div className="font-semibold text-sm text-gray-900 dark:text-gray-100">{title}</div>
            {/* Audit #3.1: one-line takeaway visible while the section is collapsed,
                so users know whether to expand without guessing. Hidden once open
                because the panel chrome already contextualizes the active report. */}
            {!open && takeaway && (
              <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 truncate">{takeaway}</div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-[11px] text-gray-400 dark:text-gray-500 font-medium">{open ? "Collapse" : "Expand"}</span>
          <svg className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>
      {open && (
        <div className="px-4 sm:px-5 pb-4 sm:pb-5 border-t dark:border-gray-800 space-y-3 pt-3">
          <div className="border-b border-gray-200 dark:border-gray-800">
            <ThinScroll className="flex gap-1 overflow-x-auto pb-1.5 md:pb-0 -mb-px" role="tablist">
              {tabs.map((tab) => {
                const isActive = tab.key === activeTab.key;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => onChange(tab.key)}
                    className={`px-3 py-2 text-sm whitespace-nowrap border-b-2 transition-colors ${
                      isActive
                        ? "border-[#32B1FF] text-gray-900 dark:text-gray-100 font-medium"
                        : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-700"
                    }`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </ThinScroll>
          </div>
          <AnalysisPanelContext.Provider value={{ forceOpen: true, hideHeader: false }}>
            <div>{activeTab.node}</div>
          </AnalysisPanelContext.Provider>
        </div>
      )}
    </div>
  );
};

const CategoryTabsSection = ({
  state,
  categories,
  usableCategories,
  selectedCategory,
  onSelectCategory,
}) => {
  if (state.loading) {
    return (
      <div className="border-b border-gray-200 dark:border-gray-800">
        <div className="flex gap-2 py-2">
          <Skeleton className="h-8 w-28" />
          <Skeleton className="h-8 w-24" />
          <Skeleton className="h-8 w-20" />
        </div>
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="rounded-xl border border-red-200 dark:border-red-900/50 bg-white dark:bg-gray-900 p-4 text-sm text-red-600 dark:text-red-400">
        {state.error}
      </div>
    );
  }
  if (!categories.length) return null;

  // Brian's Sept-2026 fix: ci_get_available_filters now returns only categories the client
  // sells, so every code in `categories` renders a tab (the old "usability probe" that hid
  // dead tabs is gone — see the lightweight safeguard effect above). "All" (code: null) is
  // always present regardless of individual category data. The empty-check above still
  // keys off the full `categories` list, so a genuinely category-less client hides the
  // whole bar while an all-categories-empty client still shows "All".
  const usableSet = new Set(usableCategories);
  const options = [
    { code: null, label: "All" },
    ...sortCategories(categories)
      .filter((code) => usableSet.has(code))
      .map((code) => ({ code, label: categoryLabel(code) })),
  ];

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm text-gray-600 dark:text-gray-300">Category:</span>
        <div className="flex items-center gap-1.5 flex-wrap">
          {options.map((option) => {
            const active = selectedCategory === option.code;
            return (
              <button
                key={option.code ?? "__all__"}
                type="button"
                onClick={() => onSelectCategory(option.code)}
                className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                  active
                    ? "border-[#32B1FF] bg-[#32B1FF]/10 text-[#32B1FF]"
                    : "border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                }`}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CompetitiveIntelligencePage;
